import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import {
  User as UserIcon, Calendar, Ruler, Activity, Target, Utensils,
  Sparkles, Sliders, Zap, Droplet, Footprints, Flame, ShieldAlert,
  CheckCircle2, XCircle, TrendingDown, TrendingUp, Minus, Info,
} from "lucide-react";

/**
 * TraineeProfileCard — coach-facing "at a glance" summary shown at the top of
 * the shared Progress page (isSharedMode only). Bundles every input the coach
 * needs to interpret the charts: onboarding profile, calc mode, macro targets,
 * exercise-inclusion toggle, adherence signals, safety flags.
 *
 * All copy is bilingual — a coach viewing an Arabic-speaking trainee in an
 * English UI sees the labels in English; the trainee's actual data (weight,
 * numbers, dates) is language-neutral.
 */
export default function TraineeProfileCard({ user }) {
  const { lang } = useI18n();
  const isEn = lang === "en";
  const dir = isEn ? "ltr" : "rtl";
  const align = isEn ? "text-left" : "text-right";
  const T = getTranslations(isEn);

  const [stats, setStats] = useState(null);
  const [open, setOpen]   = useState(true);   // expanded by default for coaches

  useEffect(() => {
    api.get("/user/profile-stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  const bmi = useMemo(() => {
    if (!user?.current_weight_kg || !user?.height_cm) return null;
    const m = user.height_cm / 100;
    return +(user.current_weight_kg / (m * m)).toFixed(1);
  }, [user?.current_weight_kg, user?.height_cm]);

  const goalLabel = ({
    lose: { text: T.goalLose,   icon: <TrendingDown className="w-3 h-3" />, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
    gain: { text: T.goalGain,   icon: <TrendingUp   className="w-3 h-3" />, color: "text-sky-600 bg-sky-50 border-sky-200" },
    maintain: { text: T.goalMaintain,  icon: <Minus className="w-3 h-3" />, color: "text-neutral-700 bg-neutral-100 border-neutral-200" },
  })[user?.goal] || { text: "—", icon: null, color: "text-neutral-500 bg-neutral-50 border-neutral-200" };

  // Activity level — represents the user's baseline daily movement (steps,
  // standing, chores) EXCLUDING logged workouts. Workouts are tracked
  // separately in `exercise_entries` and added on top of this baseline for
  // TDEE. Keeping the hint step-only avoids the confusing "3-5 workouts"
  // number contradicting what the user actually logs.
  const activityMap = {
    sedentary:   { label: T.actSedentary,   hint: T.actHintSedentary },
    light:       { label: T.actLight,       hint: T.actHintLight },
    moderate:    { label: T.actModerate,    hint: T.actHintModerate },
    active:      { label: T.actActive,      hint: T.actHintActive },
    very_active: { label: T.actVeryActive,  hint: T.actHintVeryActive },
  };
  const activityInfo = activityMap[user?.activity_level] || { label: user?.activity_level || "—", hint: "" };
  const activityLabel = activityInfo.label;

  const dietLabel = {
    classic:      T.dietClassic,
    high_protein: T.dietHighProtein,
    keto:         T.dietKeto,
    low_carb:     T.dietLowCarb,
    mediterranean: T.dietMed,
  }[user?.diet_preference] || user?.diet_preference || T.dietDefault;

  const genderLabel = user?.gender === "male" ? T.male : user?.gender === "female" ? T.female : "—";
  const calcModeAuto = user?.calc_mode !== "manual";
  const inclExercise = !!user?.include_exercise_calories;

  const adherencePct = (n, total = 7) => Math.round((Math.max(0, Math.min(total, n || 0)) / total) * 100);
  const mealAdh   = adherencePct(stats?.adherence?.meal_days_last_7);
  const waterAdh  = adherencePct(stats?.adherence?.water_days_last_7);
  const exAdh     = adherencePct(stats?.adherence?.exercise_days_last_7);
  const mealCounts = stats?.meal_type_counts_last_7 || {};
  const totalMeals = (mealCounts.breakfast || 0) + (mealCounts.lunch || 0)
                   + (mealCounts.dinner    || 0) + (mealCounts.snack || 0);

  return (
    <div className="px-5 mb-4" dir={dir}>
      <div className="bg-white rounded-3xl shadow-card overflow-hidden border-[1.5px] border-black">
        {/* Header */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-brand-50 to-orange-50 hover:brightness-105"
          data-testid="trainee-profile-toggle"
        >
          <div className="w-10 h-10 rounded-2xl bg-brand-500 text-white flex items-center justify-center shadow-sm">
            <UserIcon className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className={`flex-1 ${align}`}>
            <div className="text-sm font-black text-neutral-900">{T.traineeProfile}</div>
            <div className="text-[11px] text-neutral-600">
              {T.settingsAnd7d}
            </div>
          </div>
          <div className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${calcModeAuto ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {T.calcMode}: {calcModeAuto ? T.auto : T.manual}
          </div>
        </button>

        {open && (
          <div className="p-4 space-y-3" data-testid="trainee-profile-content">
            {/* Trainee identity — avatar + name + member ID */}
            <div className="flex items-center gap-3 bg-neutral-50 rounded-2xl p-3" data-testid="profile-identity">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-white border-2 border-white shadow-sm">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full orange-gradient flex items-center justify-center text-white font-black text-xl">
                    {(user?.full_name || user?.email || "?")[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-neutral-900 truncate">{user?.full_name || "—"}</div>
                {user?.member_id && (
                  <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold text-brand-700 bg-brand-50 border border-brand-200 px-2 py-0.5 rounded-full num" dir="ltr" data-testid="profile-member-id-badge">
                    <span>{user.member_id}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Safety alert */}
            {stats?.safety?.below_min_safe && (
              <div className="rounded-2xl bg-red-50 border-[1.5px] border-red-200 p-3 flex items-start gap-2" data-testid="profile-safety-alert">
                <ShieldAlert className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <div className="text-[11px] text-red-800 leading-relaxed">
                  <div className="font-black mb-0.5">{T.safetyTitle}</div>
                  {isEn ? (
                    <>
                      Daily target (<span className="num">{user?.daily_calories}</span> kcal) is below
                      the minimum safe level of <span className="num">{stats.safety.min_safe_calories}</span>.
                      Consider reviewing the plan.
                    </>
                  ) : (
                    <>
                      الهدف اليومي (<span className="num">{user?.daily_calories}</span> سعرة) أقل من
                      الحد الأدنى الآمن <span className="num">{stats.safety.min_safe_calories}</span>.
                      ينصح بمراجعة الخطة.
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Basic profile row */}
            <div className="grid grid-cols-4 gap-2" data-testid="profile-basics">
              <Stat icon={<UserIcon className="w-3.5 h-3.5" />} label={T.gender}  value={genderLabel} />
              <Stat icon={<Calendar className="w-3.5 h-3.5" />} label={T.age}     value={user?.age ? `${user.age} ${T.yearsUnit}` : "—"} />
              <Stat icon={<Ruler    className="w-3.5 h-3.5" />} label={T.height}  value={user?.height_cm ? `${user.height_cm} ${T.cmUnit}` : "—"} />
              <Stat icon={<Activity className="w-3.5 h-3.5" />} label={T.activity} value={activityLabel} />
            </div>

            {/* Activity DEEP details */}
            {activityInfo.hint && (
              <div className="rounded-2xl bg-sky-50 border border-sky-100 p-3" data-testid="profile-activity-details">
                <div className="text-[11px] font-bold text-sky-800 mb-1.5 flex items-center gap-1">
                  <Activity className="w-3.5 h-3.5" /> {T.activityDetails}
                </div>
                <div className="text-[11px] text-sky-900 leading-relaxed mb-2">
                  {activityInfo.hint}
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <MiniInfo
                    icon={<Footprints className="w-3 h-3 text-sky-600" />}
                    label={T.stepsGoal}
                    value={
                      user?.steps_target
                        ? (user.steps_target >= 10000
                            ? `${(user.steps_target / 1000).toFixed(user.steps_target % 1000 === 0 ? 0 : 1)}K`
                            : user.steps_target.toLocaleString("en"))
                        : "—"
                    }
                  />
                  <MiniInfo
                    icon={<Zap className="w-3 h-3 text-amber-500" />}
                    label={T.workoutsPerWeek}
                    value={(() => {
                      if (user?.workout_frequency != null && user.workout_frequency !== "")
                        return String(user.workout_frequency);
                      const days = stats?.adherence?.exercise_days_last_7;
                      if (days != null) return `${days} ${T.actualSuffix}`;
                      return "—";
                    })()}
                  />
                  <MiniInfo
                    icon={<Sliders className="w-3 h-3 text-neutral-500" />}
                    label={T.tdeeMultiplier}
                    value={({
                      sedentary: "1.20",
                      light: "1.375",
                      moderate: "1.55",
                      active: "1.725",
                      very_active: "1.9",
                    })[user?.activity_level] || "—"}
                  />
                </div>
              </div>
            )}

            {/* Goal + rate + mode toggles */}
            <div className="grid grid-cols-2 gap-2">
              <div className={`rounded-2xl p-3 border ${goalLabel.color}`} data-testid="profile-goal">
                <div className="text-[10px] font-semibold mb-1 flex items-center gap-1 opacity-80">
                  <Target className="w-3 h-3" /> {T.goal}
                </div>
                <div className="text-sm font-black flex items-center gap-1">
                  {goalLabel.icon}
                  {goalLabel.text}
                </div>
                {/* Show the EFFECTIVE weekly rate implied by the user's current
                    plan — computed from (TDEE − daily_calories) rather than the
                    stale onboarding `weekly_change_kg`. */}
                {(() => {
                  if (user?.goal === "maintain") return null;
                  const tdee = Number(user?.tdee) || 0;
                  const daily = Number(user?.daily_calories) || 0;
                  const dailyDelta = user?.goal === "gain"
                    ? Math.max(0, daily - tdee)
                    : Math.max(0, tdee - daily);
                  const effWeeklyKg = (dailyDelta * 7) / 7700;
                  const fallback = Number(user?.weekly_change_kg) || 0;
                  const shown = effWeeklyKg > 0.03 ? effWeeklyKg : fallback;
                  if (!shown) return null;
                  const unitLabel = user?.weight_unit === "lb" ? T.lbUnit : T.kgUnit;
                  const shownConverted = user?.weight_unit === "lb" ? shown * 2.20462 : shown;
                  return (
                    <div className="text-[10px] font-semibold mt-1 opacity-80 flex items-center gap-1 flex-wrap" dir={dir}>
                      <span className="num" dir="ltr">{shownConverted.toFixed(2)}</span>
                      <span>{unitLabel} / {T.perWeek}</span>
                      {user?.calc_mode === "manual" && (
                        <span className="opacity-70">({T.manualPlan})</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="rounded-2xl p-3 border bg-neutral-50 border-neutral-200" data-testid="profile-diet">
                <div className="text-[10px] font-semibold mb-1 flex items-center gap-1 text-neutral-600">
                  <Utensils className="w-3 h-3" /> {T.dietPref}
                </div>
                <div className="text-sm font-black text-neutral-900">{dietLabel}</div>
                <div className="text-[10px] font-semibold mt-1 text-neutral-500 flex items-center gap-1">
                  {inclExercise ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <XCircle className="w-3 h-3 text-neutral-400" />}
                  <span>{inclExercise ? T.exerciseIncluded : T.exerciseExcluded}</span>
                </div>
              </div>
            </div>

            {/* BMR / TDEE / BMI */}
            <div className="grid grid-cols-3 gap-2" data-testid="profile-metabolism">
              <MetricPill icon={<Sliders className="w-3 h-3" />} label="BMR" value={estimateBmr(user)} color="text-neutral-800" />
              <MetricPill icon={<Zap     className="w-3 h-3" />} label="TDEE" value={user?.tdee} color="text-amber-700" />
              <MetricPill icon={<Info    className="w-3 h-3" />} label="BMI" value={bmi} color="text-sky-700" />
            </div>

            {/* Daily targets */}
            <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-white border border-orange-100 p-3" data-testid="profile-daily-targets">
              <div className="text-[11px] font-bold text-neutral-700 mb-2 flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 text-brand-500" /> {T.dailyTargets}
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <Target4 label={T.calories} value={user?.daily_calories} unit="kcal" color="text-brand-600" />
                <Target4 label={T.protein}  value={user?.protein_g}      unit="g"    color="text-emerald-600" />
                <Target4 label={T.carbs}    value={user?.carbs_g}        unit="g"    color="text-orange-500" />
                <Target4 label={T.fats}     value={user?.fat_g}          unit="g"    color="text-yellow-600" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <MiniInfo icon={<Droplet    className="w-3 h-3 text-sky-500" />}    label={T.water}  value={user?.water_target_ml ? `${user.water_target_ml} ${T.mlUnit}` : "—"} />
                <MiniInfo icon={<Footprints className="w-3 h-3 text-neutral-500" />} label={T.steps}  value={user?.steps_target ? `${user.steps_target}` : "—"} />
              </div>
            </div>

            {/* Adherence last 7 days */}
            {stats && (
              <div className="rounded-2xl bg-neutral-50 p-3" data-testid="profile-adherence">
                <div className="text-[11px] font-bold text-neutral-700 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-brand-500" /> {T.adherence7d}</span>
                  <span className="text-[9px] text-neutral-500">{stats.days_active} {T.daysSinceSignup}</span>
                </div>
                <div className="space-y-2">
                  <AdherenceBar label={T.mealsLogged}   days={stats.adherence.meal_days_last_7}     pct={mealAdh}  color="bg-brand-500" />
                  <AdherenceBar label={T.waterIntake}   days={stats.adherence.water_days_last_7}    pct={waterAdh} color="bg-sky-500" />
                  <AdherenceBar label={T.exercises}     days={stats.adherence.exercise_days_last_7} pct={exAdh}    color="bg-emerald-500" />
                </div>
                {totalMeals > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-200">
                    <div className="text-[10px] text-neutral-500 mb-1.5">{T.mealDistribution}</div>
                    <div className="flex gap-1.5 text-[10px]">
                      <MealChip label={T.breakfast} count={mealCounts.breakfast || 0} />
                      <MealChip label={T.lunch}     count={mealCounts.lunch     || 0} />
                      <MealChip label={T.dinner}    count={mealCounts.dinner    || 0} />
                      <MealChip label={T.snack}     count={mealCounts.snack     || 0} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Last weight update */}
            {stats?.last_weight_date && (
              <div className={`rounded-2xl p-3 flex items-center gap-2 border ${stats.days_since_last_weight > 10 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="profile-last-weight">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${stats.days_since_last_weight > 10 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {stats.days_since_last_weight > 10 ? <ShieldAlert className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                </div>
                <div className="flex-1 text-[11px]">
                  <div className="font-bold text-neutral-800">
                    {T.lastWeighIn}: <span className="num">{stats.last_weight_date}</span>
                  </div>
                  <div className={`text-[10px] ${stats.days_since_last_weight > 10 ? "text-amber-700" : "text-emerald-700"}`}>
                    {stats.days_since_last_weight === 0
                      ? T.todayGreat
                      : stats.days_since_last_weight === 1
                        ? T.yesterday
                        : (isEn
                            ? `${stats.days_since_last_weight} days ago${stats.days_since_last_weight > 10 ? " — needs an update" : ""}`
                            : `منذ ${stats.days_since_last_weight} يوم ${stats.days_since_last_weight > 10 ? "— يحتاج تحديث" : ""}`)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────
function Stat({ icon, label, value }) {
  return (
    <div className="bg-neutral-50 rounded-xl p-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[9px] text-neutral-500 mb-0.5">
        {icon}{label}
      </div>
      <div className="text-[11px] font-black text-neutral-900 num" dir="auto">{value}</div>
    </div>
  );
}

function MetricPill({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[9px] text-neutral-500 mb-0.5">
        {icon}{label}
      </div>
      <div className={`text-sm font-black num ${color}`} dir="ltr">
        {value ?? "—"}
      </div>
    </div>
  );
}

function Target4({ label, value, unit, color }) {
  return (
    <div>
      <div className={`text-base font-black num ${color}`} dir="ltr">{value ?? "—"}</div>
      <div className="text-[9px] text-neutral-500 font-semibold">{label}</div>
      <div className="text-[8px] text-neutral-400 num" dir="ltr">{unit}</div>
    </div>
  );
}

function MiniInfo({ icon, label, value }) {
  return (
    <div className="bg-white rounded-xl px-2 py-2 flex flex-col items-center gap-0.5 text-center min-w-0">
      <div className="flex items-center gap-1 text-[10px] text-neutral-500 font-semibold leading-tight">
        <span className="flex-shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className="text-[12px] font-black text-neutral-800 num whitespace-nowrap" dir="auto">{value}</div>
    </div>
  );
}

function AdherenceBar({ label, days, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <span className="text-neutral-700 font-semibold">{label}</span>
        <span className="num text-neutral-800 font-bold" dir="ltr">
          {days}/7
          <span className="text-neutral-400 mr-1">({pct}%)</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function MealChip({ label, count }) {
  return (
    <div className={`flex-1 rounded-lg px-1.5 py-1 text-center border ${count > 0 ? "bg-white border-neutral-200" : "bg-neutral-100 border-neutral-100 opacity-60"}`}>
      <div className="text-neutral-700 font-bold">{label}</div>
      <div className="num font-black text-brand-600" dir="ltr">{count}</div>
    </div>
  );
}

// Rough BMR estimate (Mifflin-St Jeor) so the coach sees the value even if the
// backend hasn't stored `bmr` on the User object.
function estimateBmr(u) {
  if (!u?.gender || !u.height_cm || !u.age || !u.current_weight_kg) return null;
  const base = 10 * u.current_weight_kg + 6.25 * u.height_cm - 5 * u.age;
  return Math.round(u.gender === "male" ? base + 5 : base - 161);
}

// ─── i18n dictionary ──────────────────────────────────────────────────
// Kept inline (rather than a separate JSON) so the component stays
// self-contained — this card is only ever rendered on the coach share
// page, so its strings don't need to live in the global i18n bundle.
function getTranslations(isEn) {
  return isEn ? {
    traineeProfile:   "Trainee profile",
    settingsAnd7d:    "All settings + 7-day adherence",
    calcMode:         "Calorie mode",
    auto:             "Auto",
    manual:           "Manual",
    male:             "Male",
    female:           "Female",
    gender:           "Gender",
    age:              "Age",
    height:           "Height",
    activity:         "Activity",
    yearsUnit:        "yrs",
    cmUnit:           "cm",
    mlUnit:           "ml",
    kgUnit:           "kg",
    lbUnit:           "lb",
    perWeek:          "week",
    manualPlan:       "manual plan",
    actSedentary:     "Low",
    actLight:         "Light",
    actModerate:      "Moderate",
    actActive:        "High",
    actVeryActive:    "Very high",
    actHintSedentary: "Under 5,000 steps / day",
    actHintLight:     "5,000–7,500 steps / day",
    actHintModerate:  "7,500–10,000 steps / day",
    actHintActive:    "10,000+ steps / day",
    actHintVeryActive:"Heavy physical work / near-constant movement",
    activityDetails:  "Daily activity details",
    stepsGoal:        "Steps goal",
    workoutsPerWeek:  "Workouts/week",
    actualSuffix:     "actual",
    tdeeMultiplier:   "TDEE multiplier",
    dietClassic:      "Balanced classic",
    dietHighProtein:  "High protein",
    dietKeto:         "Keto",
    dietLowCarb:      "Low carb",
    dietMed:          "Mediterranean",
    dietDefault:      "Balanced",
    goal:             "Goal",
    goalLose:         "Lose weight",
    goalGain:         "Gain weight",
    goalMaintain:     "Maintain",
    dietPref:         "Diet preference",
    exerciseIncluded: "Workouts counted in calories",
    exerciseExcluded: "Workouts not added to calories",
    dailyTargets:     "Daily targets",
    calories:         "Calories",
    protein:          "Protein",
    carbs:            "Carbs",
    fats:             "Fats",
    water:            "Water",
    steps:            "Steps",
    adherence7d:      "7-day adherence",
    daysSinceSignup:  "days since signup",
    mealsLogged:      "Meals logged",
    waterIntake:      "Water intake",
    exercises:        "Exercises",
    mealDistribution: "Meal distribution (7 days)",
    breakfast:        "Breakfast",
    lunch:            "Lunch",
    dinner:           "Dinner",
    snack:            "Snack",
    lastWeighIn:      "Last weigh-in",
    todayGreat:       "Today — great!",
    yesterday:        "Yesterday",
    safetyTitle:      "Warning: below the safe minimum",
  } : {
    traineeProfile:   "ملف المتدرّب",
    settingsAnd7d:    "كل الإعدادات + الالتزام آخر 7 أيام",
    calcMode:         "وضع حساب السعرات",
    auto:             "تلقائي",
    manual:           "يدوي",
    male:             "ذكر",
    female:           "أنثى",
    gender:           "الجنس",
    age:              "العمر",
    height:           "الطول",
    activity:         "النشاط",
    yearsUnit:        "سنة",
    cmUnit:           "سم",
    mlUnit:           "مل",
    kgUnit:           "كجم",
    lbUnit:           "رطل",
    perWeek:          "أسبوع",
    manualPlan:       "خطة يدوية",
    actSedentary:     "قليلة",
    actLight:         "خفيفة",
    actModerate:      "متوسطة",
    actActive:        "عالية",
    actVeryActive:    "عالية جداً",
    actHintSedentary: "أقل من 5,000 خطوة / يوم",
    actHintLight:     "5,000–7,500 خطوة / يوم",
    actHintModerate:  "7,500–10,000 خطوة / يوم",
    actHintActive:    "10,000+ خطوة / يوم",
    actHintVeryActive:"عمل بدني ثقيل / حركة شبه دائمة",
    activityDetails:  "تفاصيل النشاط اليومي",
    stepsGoal:        "هدف الخطوات",
    workoutsPerWeek:  "تمارين/أسبوع",
    actualSuffix:     "فعلي",
    tdeeMultiplier:   "مضاعِف TDEE",
    dietClassic:      "كلاسيكي متوازن",
    dietHighProtein:  "عالي بروتين",
    dietKeto:         "كيتو",
    dietLowCarb:      "قليل الكربوهيدرات",
    dietMed:          "متوسطي",
    dietDefault:      "متوازن",
    goal:             "الهدف",
    goalLose:         "إنقاص الوزن",
    goalGain:         "زيادة الوزن",
    goalMaintain:     "المحافظة",
    dietPref:         "النظام الغذائي",
    exerciseIncluded: "التمارين محسوبة بالسعرات",
    exerciseExcluded: "التمارين لا تُضاف للسعرات",
    dailyTargets:     "الأهداف اليومية",
    calories:         "سعرات",
    protein:          "بروتين",
    carbs:            "كربوهيدرات",
    fats:             "دهون",
    water:            "ماء",
    steps:            "خطوات",
    adherence7d:      "الالتزام آخر 7 أيام",
    daysSinceSignup:  "يوم منذ التسجيل",
    mealsLogged:      "تسجيل الوجبات",
    waterIntake:      "شرب الماء",
    exercises:        "تمارين",
    mealDistribution: "توزيع الوجبات (7 أيام)",
    breakfast:        "فطور",
    lunch:            "غداء",
    dinner:           "عشاء",
    snack:            "سناك",
    lastWeighIn:      "آخر قياس وزن",
    todayGreat:       "اليوم — ممتاز!",
    yesterday:        "أمس",
    safetyTitle:      "تحذير: أقل من الحد الآمن",
  };
}
